import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, Building, ArrowRight, UserPlus, Settings } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { useI18n } from "@/i18n/I18nProvider";
import { useAuth } from "@/context/AuthContext";
import { COMING_SOON_MODULES } from "@/config/navigation";
import { api, errorKey } from "@/lib/api";

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch((e) => setError(errorKey(e)));
  }, []);

  const dash = (v) => (data ? v : "—");
  return (
    <div>
      <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.welcome", { name: user.first_name })} testId="dashboard-title" />
      {error && <p data-testid="dashboard-error" className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{t(error)}</p>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
        <StatCard testId="stat-active-employees" label={t("dashboard.activeEmployees")} value={dash(data?.employees_active)} icon={UserCheck} accent />
        <StatCard testId="stat-total-employees" label={t("dashboard.totalEmployees")} value={dash(data?.employees_total)} icon={Users} />
        <StatCard testId="stat-company-profile" label={t("dashboard.companyProfile")} icon={Building}
          value={data ? (data.company_configured ? t("dashboard.configured") : t("dashboard.notConfigured")) : "—"} />
      </div>

      <h2 className="mt-10 font-display text-lg font-bold text-[#000B45]">{t("dashboard.quickActions")}</h2>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <Link to="/admin/employees?new=1" data-testid="quick-add-employee"
          className="flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-[#FEB601] px-5 text-sm font-bold text-[#000B45] transition-colors hover:bg-[#E5A300]">
          <UserPlus className="h-4 w-4" /> {t("dashboard.addEmployee")}
        </Link>
        {data && !data.company_configured && <Link to="/admin/settings" data-testid="quick-settings"
          className="flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-[#000B45] bg-white px-5 text-sm font-bold text-[#000B45] transition-colors hover:bg-[#000B45] hover:text-white">
          <Settings className="h-4 w-4" /> {t("dashboard.configureNow")}
        </Link>}
      </div>

      <h2 className="mt-10 font-display text-lg font-bold text-[#000B45]">{t("dashboard.modulesTitle")}</h2>
      <p className="text-sm text-gray-600">{t("dashboard.modulesSubtitle")}</p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {COMING_SOON_MODULES.map((m) => (
          <Link key={m.key} to={m.path} data-testid={`module-card-${m.key}`}
            className="group flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-white p-4 transition-shadow hover:shadow-md">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#000B45]/5 text-[#000B45]">
              <m.icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#111827]">{t(`nav.${m.key}`)}</p>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#8a6300]">{t("common.comingSoon")}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </div>
  );
}
